'use client';

import React from 'react';

interface StepProps {
  number: string;
  title: string;
  description: string;
  iconColor: string;
  iconBg: string;
  iconBorder: string;
  iconShadow: string;
  icon: React.ReactNode;
  delay: string;
  isLast?: boolean;
}

function StepDesktop({ number, title, description, iconColor, iconBg, iconBorder, iconShadow, icon, delay, isLast }: StepProps) {
  return (
    <>
      <div className={`reveal ${delay} flex-1 flex flex-col items-center text-center px-6`}>
        <div
          className="relative w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
          style={{ background: iconBg, border: `1px solid ${iconBorder}`, color: iconColor, boxShadow: iconShadow }}
        >
          {icon}
          <span className="absolute -top-2 -right-2 text-xs font-black text-neutral-600">{number}</span>
        </div>
        <h3 className="text-lg font-bold text-white mb-3">{title}</h3>
        <p className="text-sm text-neutral-500 leading-relaxed">{description}</p>
      </div>
      {!isLast && (
        <div className="flex-shrink-0 flex items-start pt-8 w-8">
          <div className="process-line w-full" />
        </div>
      )}
    </>
  );
}

function StepMobile({ number, title, description, iconColor, iconBg, iconBorder, icon, delay }: StepProps) {
  return (
    <div className={`reveal ${delay} glass-card rounded-2xl p-6 flex gap-5`}>
      <div
        className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ background: iconBg, border: `1px solid ${iconBorder}`, color: iconColor }}
      >
        {icon}
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs text-neutral-600 font-bold">{number}</span>
          <h3 className="text-base font-bold text-white">{title}</h3>
        </div>
        <p className="text-sm text-neutral-500 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

export default function ProcessSection() {
  const steps = [
    {
      number: '01',
      title: 'Audit',
      description: 'We deep-dive into your current content, systems, and audience data to uncover gaps, opportunities, and hidden leverage points.',
      iconColor: 'rgb(59, 130, 246)',
      iconBg: 'rgba(59, 130, 246, 0.12)',
      iconBorder: 'rgba(59, 130, 246, 0.3)',
      iconShadow: 'rgba(59, 130, 246, 0.15) 0px 0px 24px',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m21 21-4.34-4.34" />
          <circle cx="11" cy="11" r="8" />
        </svg>
      ),
      delay: 'reveal-delay-1',
    },
    {
      number: '02',
      title: 'Strategy',
      description: 'We build a custom AI-powered growth roadmap — tailored to your brand goals, audience, and competitive landscape.',
      iconColor: 'rgb(99, 102, 241)',
      iconBg: 'rgba(99, 102, 241, 0.12)',
      iconBorder: 'rgba(99, 102, 241, 0.3)',
      iconShadow: 'rgba(99, 102, 241, 0.15) 0px 0px 24px',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
      delay: 'reveal-delay-2',
    },
    {
      number: '03',
      title: 'Build',
      description: 'We implement your content systems, automation workflows, and conversion funnels — fully engineered and launch-ready.',
      iconColor: 'rgb(139, 92, 246)',
      iconBg: 'rgba(139, 92, 246, 0.12)',
      iconBorder: 'rgba(139, 92, 246, 0.3)',
      iconShadow: 'rgba(139, 92, 246, 0.15) 0px 0px 24px',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
      ),
      delay: 'reveal-delay-3',
    },
    {
      number: '04',
      title: 'Scale',
      description: 'We optimize, iterate, and amplify what is working — turning your growth engine into a compounding, unstoppable machine.',
      iconColor: 'rgb(157, 76, 221)',
      iconBg: 'rgba(157, 76, 221, 0.12)',
      iconBorder: 'rgba(157, 76, 221, 0.3)',
      iconShadow: 'rgba(157, 76, 221, 0.15) 0px 0px 24px',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
        </svg>
      ),
      delay: 'reveal-delay-4',
      isLast: true,
    },
  ];

  return (
    <section
      id="process"
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{ background: 'rgb(5, 5, 5)' }}
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div style={{ width: 600, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99, 102, 241, 0.07) 0%, transparent 70%)', filter: 'blur(50px)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal text-center mb-20">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-400 mb-4 block">How We Work</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" style={{ letterSpacing: '-0.02em' }}>
            From Zero to Scaled — <span className="gradient-text">in 4 Steps</span>
          </h2>
          <p className="text-base text-neutral-500 max-w-lg mx-auto">A proven framework that takes brands from chaos to compounding growth.</p>
        </div>

        {/* Desktop Steps */}
        <div className="hidden lg:flex items-start gap-0 mb-8">
          {steps.map((step) => (
            <StepDesktop key={step.number} {...step} />
          ))}
        </div>

        {/* Mobile Steps */}
        <div className="lg:hidden space-y-6">
          {steps.map((step) => (
            <StepMobile key={step.number} {...step} />
          ))}
        </div>
      </div>
    </section>
  );
}
